import { services } from 'services/base';
import { UnsubscribeMethod } from 'types';
import { upperCamelCase } from 'utils/helpers';

export type GetStateService<Value> = () => Value;
export type SetStateService<Value> = (value: Value) => void;

export type OnStateChangeServiceCallback<Value> = (value: Value) => void;
export type OnStateChangeService<Value> = (callback: OnStateChangeServiceCallback<Value[]>) => UnsubscribeMethod;

export type RegisterStateServiceMethods = {
  set?: string | null;
  get?: string | null;
  onChange?: string | null;
};
export type RegisterStateOptions<StateKey extends string, Value extends unknown> = {
  state: { [key in StateKey]: Value };
  stateKey: StateKey;
  initValue?: Value;
  servicePrefix: string;
  serviceMethods?: RegisterStateServiceMethods;
};

export type RegisterStateServicesResult<Value extends unknown> = {
  setStateActionName: string;
  getter: () => Value;
  setter: (value: Value) => void;
  subscribe: OnStateChangeService<Value>;
};

const registerStateServices = <StateKey extends string, Value extends unknown>({
  state,
  stateKey,
  initValue,
  servicePrefix: prefix,
  serviceMethods: { get = 'get', set = 'set', onChange = 'onChange' } = {},
}: RegisterStateOptions<StateKey, Value>): RegisterStateServicesResult<Value> => {
  const { registerAction, clearAction, registerService, executeAction } = services;

  const setStateActionName = `${prefix.split('.').map(upperCamelCase).join(':')}:${upperCamelCase(set ?? 'Set')}`;
  if (initValue !== undefined) {
    state[stateKey] = initValue;
  }

  const getter = () => state[stateKey];
  if (get) {
    registerService<GetStateService<Value>>(`${prefix}.${get}`, getter);
  }

  registerAction<Value>(
    setStateActionName,
    (newValue) => {
      state[stateKey] = newValue;
    },
    { order: 0 },
  );

  const setter = (newValue: Value) => {
    executeAction(setStateActionName, newValue);
  };
  if (set) {
    registerService<SetStateService<Value>>(`${prefix}.${set}`, setter);
  }

  const subscribe: OnStateChangeService<Value> = (callback) => {
    registerAction(setStateActionName, callback, { order: 1 });
    return () => clearAction(setStateActionName, callback);
  };
  if (onChange) {
    registerService<OnStateChangeService<Value>>(`${prefix}.${onChange}`, subscribe);
  }

  return { setStateActionName, getter, setter, subscribe };
};

export default registerStateServices;
